import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import attendanceService from '../../../services/attendanceService';

const EmployeeAttendanceDetailModal = ({
  isOpen,
  onClose,
  employee,
  selectedPeriod
}) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && employee?.id) {
      fetchEmployeeRecords();
    }
  }, [isOpen, employee, selectedPeriod]);

  const fetchEmployeeRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const [year, month] = selectedPeriod.split('-');
      const dateFrom = `${year}-${month}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const dateTo = `${year}-${month}-${lastDay}`;

      const res = await attendanceService.getAttendanceRecords({
        employee_id: employee.id,
        date_from: dateFrom,
        date_to: dateTo
      });

      if (res.success && res.data) {
        setRecords(res.data);
      } else {
        setRecords([]);
      }
    } catch (err) {
      setError('Failed to fetch detailed records');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-3xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-base border border-primary/20">
              {employee.full_name?.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{employee.full_name}</h2>
              <p className="text-xs text-muted-foreground">
                {employee.employee_code} • {employee.department} • {employee.position}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Summary Badges Header */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-emerald-700">Days Present</div>
              <div className="text-xl font-bold text-emerald-600">{employee.days_present}</div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-amber-700">Late Arrivals</div>
              <div className="text-xl font-bold text-amber-600">{employee.days_late}</div>
            </div>
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-rose-700">Days Absent</div>
              <div className="text-xl font-bold text-rose-600">{employee.days_absent}</div>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-purple-700">Total Work Hours</div>
              <div className="text-xl font-bold text-purple-600">{employee.work_hours} h</div>
            </div>
          </div>

          {/* Attendance Log Table */}
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Calendar" size={16} className="text-primary" />
            Daily Attendance Log ({selectedPeriod})
          </h3>

          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Loading attendance log...</div>
          ) : error ? (
            <div className="py-8 text-center text-rose-500 text-sm">{error}</div>
          ) : records.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm border border-dashed rounded-lg">
              No daily records punched for this period yet.
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b border-border text-muted-foreground font-semibold">
                    <th className="py-2.5 px-3">Date</th>
                    <th className="py-2.5 px-3">Clock In</th>
                    <th className="py-2.5 px-3">Clock Out</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3 text-right">Work Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {records.map((r, i) => (
                    <tr key={r.id || i} className="hover:bg-muted/20">
                      <td className="py-2.5 px-3 font-medium">{r.attendance_date}</td>
                      <td className="py-2.5 px-3">{r.clock_in_time ? new Date(r.clock_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                      <td className="py-2.5 px-3">{r.clock_out_time ? new Date(r.clock_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                      <td className="py-2.5 px-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${
                          r.status === 'present' ? 'bg-emerald-500/10 text-emerald-600' :
                          r.status === 'late' ? 'bg-amber-500/10 text-amber-600' :
                          r.status === 'absent' ? 'bg-rose-500/10 text-rose-600' : 'bg-muted text-muted-foreground'
                        }`}>
                          {r.status || 'present'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono">{r.work_hours || 0} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end bg-muted/20">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendanceDetailModal;
